// src/mainframes/sidebar/QuickLinks.jsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Avatar,
  Divider,
  Tooltip,
  CircularProgress,
  Alert,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Plus, Search, X, Upload, Trash2, ExternalLink, RefreshCw } from "lucide-react";

import {
  useGetMyQuickLinksQuery,
  useCreateQuickLinkMutation,
  useDeleteQuickLinkMutation,
  useUploadQuickLinkLogoMutation,
} from "../../redux/admin/userApi";

import { useLang } from "../../i18n/LanguageProvider";

const isValidHttpUrl = (value) => {
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

const QuickLinks = () => {
  const theme = useTheme();
  const { t } = useLang();

  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  // form state
  const [name, setName] = useState("");
  const [link, setLink] = useState("");
  const [status, setStatus] = useState(true);
  const [file, setFile] = useState(null);

  // basic validation errors
  const [formError, setFormError] = useState("");

  const {
    data: rows = [],
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetMyQuickLinksQuery(q);

  const [createQuickLink, { isLoading: creating }] = useCreateQuickLinkMutation();
  const [uploadLogo, { isLoading: uploading }] = useUploadQuickLinkLogoMutation();
  const [deleteQuickLink, { isLoading: deleting }] = useDeleteQuickLinkMutation();

  const busy = isLoading || isFetching || creating || uploading || deleting;

  const filePreview = useMemo(() => {
    if (!file) return "";
    return URL.createObjectURL(file);
  }, [file]);

  const resetForm = () => {
    setName("");
    setLink("");
    setStatus(true);
    setFile(null);
    setFormError("");
  };

  const handleOpen = () => {
    resetForm();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormError("");
  };

  const handleSave = async () => {
    setFormError("");

    const n = name.trim();
    const l = link.trim();

    if (!n) return setFormError(t("NAME_REQUIRED", "Name is required"));
    if (!l) return setFormError(t("LINK_REQUIRED", "Link is required"));
    if (!isValidHttpUrl(l)) return setFormError(t("LINK_INVALID", "Link must be a valid http/https URL"));

    try {
      // 1) create quicklink
      const created = await createQuickLink({ name: n, link: l, status }).unwrap();
      const createdId = created?.data?._id;

      // 2) upload logo if present
      if (createdId && file) {
        const formData = new FormData();
        formData.append("logo", file);
        await uploadLogo({ id: createdId, formData }).unwrap();
      }

      setOpen(false);
      resetForm();
      refetch();
    } catch (e) {
      const msg =
        e?.data?.message ||
        e?.error ||
        t("SOMETHING_WRONG", "Something went wrong");
      setFormError(msg);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteQuickLink(id).unwrap();
      refetch();
    } catch (e) {
      // no hard fail UI
      console.error("delete quicklink error", e);
    }
  };

  const errorText =
    error?.data?.message ||
    error?.error ||
    t("SOMETHING_WRONG", "Something went wrong");

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        gap={1.2}
        sx={{ mb: 1.5 }}
      >
        <Typography variant="h4" sx={{ fontWeight: 900 }}>
          {t("QUICK_LINKS", "Quick Links")}
        </Typography>

        <Stack direction="row" gap={1} alignItems="center" justifyContent="flex-end">
          <TextField
            size="small"
            placeholder={t("SEARCH", "Search")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sx={{ width: { xs: "100%", sm: 280 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
            }}
          />

          <Tooltip title={t("ADD", "Add")}>
            <Button
              variant="contained"
              onClick={handleOpen}
              sx={{
                minWidth: 44,
                px: 2,
                borderRadius: 2,
                fontWeight: 900,
                bgcolor: "black",
                "&:hover": { bgcolor: "#111" },
              }}
            >
              <Plus size={18} />
            </Button>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Error */}
      {isError && (
        <Alert
          severity="error"
          sx={{ mb: 1.5 }}
          action={
            <Button
              size="small"
              variant="outlined"
              onClick={() => refetch()}
              startIcon={<RefreshCw size={16} />}
              sx={{ fontWeight: 900 }}
            >
              {t("RETRY", "Retry")}
            </Button>
          }
        >
          {errorText}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box
          sx={{
            px: 2,
            py: 1.2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          }}
        >
          <Typography sx={{ fontWeight: 900 }}>
            {t("QUICK_LINKS", "Quick Links")}
          </Typography>

          <Stack direction="row" alignItems="center" gap={1}>
            {(isLoading || isFetching) && <CircularProgress size={18} />}
            <Tooltip title={t("REFRESH", "Refresh")}>
              <IconButton size="small" onClick={() => refetch()}>
                <RefreshCw size={16} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
              <TableCell sx={{ fontWeight: 900 }}>{t("NAME", "Name")}</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>{t("LINK", "Link")}</TableCell>
              <TableCell sx={{ fontWeight: 900, width: 120 }}>{t("LOGO", "Logo")}</TableCell>
              <TableCell sx={{ fontWeight: 900, width: 160 }}>{t("ACTION", "Action")}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!rows?.length ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 6, color: "text.secondary" }}>
                  {t("NO_DATA", "No Data In Table")}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r._id} hover>
                  <TableCell sx={{ fontWeight: 800 }}>{r.name}</TableCell>

                  <TableCell sx={{ maxWidth: 520 }}>
                    <Stack direction="row" gap={1} alignItems="center" sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                        }}
                        title={r.link}
                      >
                        {r.link}
                      </Typography>

                      <Tooltip title={t("OPEN", "Open")}>
                        <IconButton
                          size="small"
                          onClick={() => window.open(r.link, "_blank", "noopener,noreferrer")}
                        >
                          <ExternalLink size={16} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>

                  <TableCell>
                    <Avatar
                      variant="rounded"
                      src={r.logoUrl || ""}
                      alt={r.name}
                      sx={{
                        width: 46,
                        height: 30,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                        border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                      }}
                    >
                      {r.name?.slice(0, 1)?.toUpperCase()}
                    </Avatar>
                  </TableCell>

                  <TableCell>
                    <Stack direction="row" gap={1} alignItems="center">
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 900,
                          px: 1,
                          py: 0.4,
                          borderRadius: 999,
                          border: `1px solid ${
                            r.status
                              ? alpha(theme.palette.success.main, 0.35)
                              : alpha(theme.palette.error.main, 0.35)
                          }`,
                          color: r.status ? theme.palette.success.main : theme.palette.error.main,
                          bgcolor: r.status
                            ? alpha(theme.palette.success.main, 0.08)
                            : alpha(theme.palette.error.main, 0.06),
                        }}
                      >
                        {r.status ? t("ACTIVE", "Active") : t("INACTIVE", "Inactive")}
                      </Typography>

                      <Tooltip title={t("DELETE", "Delete")}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(r._id)}
                            disabled={busy}
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Add Modal */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontWeight: 900,
          }}
        >
          {t("QUICK_LINKS", "Quick Links")}
          <IconButton onClick={handleClose} size="small">
            <X size={18} />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2 }}>
          <Stack gap={2}>
            {formError && <Alert severity="error">{formError}</Alert>}

            <TextField
              label={t("NAME", "Name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("ENTER_NAME", "Enter name")}
              fullWidth
            />

            <TextField
              label={t("LINK", "Link")}
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder={t("ENTER_URL", "Enter URL")}
              fullWidth
            />

            <FormControlLabel
              control={<Checkbox checked={status} onChange={(e) => setStatus(e.target.checked)} />}
              label={t("STATUS", "Status")}
            />

            {/* Logo uploader */}
            <Box>
              <Typography sx={{ fontWeight: 900, mb: 1 }}>
                {t("LOGO", "Logo")}
              </Typography>

              <Stack direction="row" gap={1.2} alignItems="center">
                <Avatar
                  variant="rounded"
                  src={filePreview || ""}
                  sx={{
                    width: 64,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                  }}
                />

                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<Upload size={16} />}
                  sx={{ borderRadius: 2, fontWeight: 900 }}
                >
                  {t("UPLOAD", "Upload")}
                  <input
                    type="file"
                    hidden
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                </Button>

                {file && (
                  <Button
                    variant="text"
                    onClick={() => setFile(null)}
                    sx={{ fontWeight: 900 }}
                  >
                    {t("REMOVE", "Remove")}
                  </Button>
                )}
              </Stack>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.8 }}
              >
                {t("LOGO_HINT", "PNG/JPG/WEBP up to 2MB")}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{ borderRadius: 2, fontWeight: 900 }}
          >
            {t("CANCEL", "Cancel")}
          </Button>

          <Button
            onClick={handleSave}
            variant="contained"
            disabled={busy}
            sx={{
              borderRadius: 2,
              fontWeight: 900,
              bgcolor: "#f84f6a",
              "&:hover": { bgcolor: "#e6455f" },
            }}
          >
            {busy ? t("SAVING", "SAVING...") : t("SAVE", "SAVE")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuickLinks;
